/**
 * Experience Management Service
 * Handles all work experience-related operations including job history,
 * projects, achievements, and career progression tracking
 */

// Mock AsyncStorage for environments where it's not available
let AsyncStorage;
try {
  AsyncStorage = require('@react-native-async-storage/async-storage').default;
} catch (error) {
  console.warn('AsyncStorage not available, using mock implementation');
  AsyncStorage = {
    getItem: async (key) => null,
    setItem: async (key, value) => {},
    multiGet: async (keys) => keys.map(key => [key, null]),
    removeItem: async (key) => {}
  };
}

class ExperienceManagementService {
  constructor() {
    this.isInitialized = false;
    this.experienceData = {
      workExperience: [],
      projects: [],
      achievements: [],
      references: [],
      companies: []
    };
    this.subscribers = new Map();
    this.offlineQueue = [];
  }

  // Initialize the service
  async initialize() {
    try {
      console.log('🚀 Initializing Experience Management Service...');
      
      // Load cached experience data
      await this.loadCachedData();
      
      // Initialize companies database
      await this.initializeCompanies();
      
      this.isInitialized = true;
      console.log('✅ Experience Management Service initialized successfully');
      
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize Experience Management Service:', error);
      return false;
    }
  }

  // Load cached experience data
  async loadCachedData() {
    try {
      const keys = [
        'work_experience',
        'user_projects',
        'work_achievements',
        'user_references'
      ];
      
      const cachedData = await AsyncStorage.multiGet(keys);
      
      const KEY_MAP = {
        'work_experience':   'workExperience',
        'user_projects':     'projects',
        'work_achievements': 'achievements',
        'user_references':   'references',
      };
      cachedData.forEach(([key, value]) => {
        if (value) {
          try {
            const dataKey = KEY_MAP[key] || key;
            const parsed = JSON.parse(value);
            this.experienceData[dataKey] = Array.isArray(parsed) ? parsed : [];
          } catch (parseError) {
            console.warn(`Failed to parse cached ${key}:`, parseError.message);
          }
        }
      });
      
      console.log('📦 Experience cached data loaded');
    } catch (error) {
      console.error('❌ Error loading cached experience data:', error);
    }
  }

  // Initialize companies database
  async initializeCompanies() {
    try {
      const companies = [
        {
          id: 'comp_1',
          name: 'Google',
          industry: 'Technology',
          size: 'Large (10,000+ employees)',
          location: 'Mountain View, CA',
          verified: true
        },
        {
          id: 'comp_2',
          name: 'Microsoft',
          industry: 'Technology',
          size: 'Large (10,000+ employees)',
          location: 'Redmond, WA',
          verified: true
        },
        {
          id: 'comp_3',
          name: 'Apple',
          industry: 'Technology',
          size: 'Large (10,000+ employees)',
          location: 'Cupertino, CA',
          verified: true
        },
        {
          id: 'comp_4',
          name: 'Amazon',
          industry: 'E-commerce/Technology',
          size: 'Large (10,000+ employees)',
          location: 'Seattle, WA',
          verified: true
        }
      ];
      
      this.experienceData.companies = companies;
      await AsyncStorage.setItem('companies', JSON.stringify(companies));
      
    } catch (error) {
      console.error('❌ Error initializing companies:', error);
    }
  }

  // Get user work experience
  async getUserExperience(userId) {
    try {
      console.log('💼 Getting user experience for:', userId);
      
      return {
        success: true,
        data: {
          workExperience:  this.experienceData.workExperience  || [],
          projects:        this.experienceData.projects        || [],
          achievements:    this.experienceData.achievements    || [],
          references:      this.experienceData.references      || [],
          total:           (this.experienceData.workExperience || []).length
                         + (this.experienceData.projects || []).length,
        }
      };
      
    } catch (error) {
      console.error('❌ Error getting user experience:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Add work experience
  async addWorkExperience(userId, experienceData) {
    try {
      console.log('💼 Adding work experience for:', userId);
      
      const newExperience = {
        id: `exp_${Date.now()}`,
        ...experienceData,
        userId,
        dateAdded: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        verified: false,
        skills: experienceData.skills || [],
        achievements: experienceData.achievements || []
      };
      
      this.experienceData.workExperience.push(newExperience);
      await this.saveWorkExperience();
      
      // Notify subscribers
      this.notifySubscribers('experience_added', newExperience);
      
      return {
        success: true,
        data: newExperience
      };
      
    } catch (error) {
      console.error('❌ Error adding work experience:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Update work experience
  async updateWorkExperience(userId, experienceId, updates) {
    try {
      console.log('📝 Updating work experience:', experienceId);
      
      const expIndex = (this.experienceData.workExperience || []).findIndex(exp => exp.id === experienceId);
      
      if (expIndex === -1) {
        throw new Error('Work experience not found');
      }
      
      this.experienceData.workExperience[expIndex] = {
        ...this.experienceData.workExperience[expIndex],
        ...updates,
        lastUpdated: new Date().toISOString()
      };
      
      await this.saveWorkExperience();
      
      // Notify subscribers
      this.notifySubscribers('experience_updated', this.experienceData.workExperience[expIndex]);
      
      return {
        success: true,
        data: this.experienceData.workExperience[expIndex]
      };
      
    } catch (error) {
      console.error('❌ Error updating work experience:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Delete work experience
  async deleteWorkExperience(userId, experienceId) {
    try {
      console.log('🗑️ Deleting work experience:', experienceId);
      
      const expIndex = (this.experienceData.workExperience || []).findIndex(exp => exp.id === experienceId);
      
      if (expIndex === -1) {
        throw new Error('Work experience not found');
      }
      
      const deletedExperience = this.experienceData.workExperience.splice(expIndex, 1)[0];
      await this.saveWorkExperience();
      
      // Notify subscribers
      this.notifySubscribers('experience_deleted', { experienceId, experience: deletedExperience });
      
      return {
        success: true,
        data: { experienceId }
      };
      
    } catch (error) {
      console.error('❌ Error deleting work experience:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Add project
  async addProject(userId, projectData) {
    try {
      console.log('🚀 Adding project for:', userId);
      
      const newProject = {
        id: `project_${Date.now()}`,
        ...projectData,
        userId,
        dateAdded: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        status: projectData.status || 'completed',
        technologies: projectData.technologies || [],
        teamSize: projectData.teamSize || 1
      };
      
      this.experienceData.projects.push(newProject);
      await this.saveProjects();
      
      // Notify subscribers
      this.notifySubscribers('project_added', newProject);
      
      return {
        success: true,
        data: newProject
      };
      
    } catch (error) {
      console.error('❌ Error adding project:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Update project
  async updateProject(userId, projectId, updates) {
    try {
      console.log('📝 Updating project:', projectId);
      
      const projectIndex = (this.experienceData.projects || []).findIndex(project => project.id === projectId);
      
      if (projectIndex === -1) {
        throw new Error('Project not found');
      }
      
      this.experienceData.projects[projectIndex] = {
        ...this.experienceData.projects[projectIndex],
        ...updates,
        lastUpdated: new Date().toISOString()
      };
      
      await this.saveProjects();
      
      // Notify subscribers
      this.notifySubscribers('project_updated', this.experienceData.projects[projectIndex]);
      
      return {
        success: true,
        data: this.experienceData.projects[projectIndex]
      };
      
    } catch (error) {
      console.error('❌ Error updating project:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Delete project
  async deleteProject(userId, projectId) {
    try {
      console.log('🗑️ Deleting project:', projectId);
      
      const projectIndex = (this.experienceData.projects || []).findIndex(project => project.id === projectId);
      
      if (projectIndex === -1) {
        throw new Error('Project not found');
      }
      
      const deletedProject = this.experienceData.projects.splice(projectIndex, 1)[0];
      await this.saveProjects();
      
      // Notify subscribers
      this.notifySubscribers('project_deleted', { projectId, project: deletedProject });
      
      return {
        success: true,
        data: { projectId }
      };
      
    } catch (error) {
      console.error('❌ Error deleting project:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Add achievement
  async addAchievement(userId, achievementData) {
    try {
      console.log('🏆 Adding achievement for:', userId);
      
      const newAchievement = {
        id: `achievement_${Date.now()}`,
        ...achievementData,
        userId,
        dateAdded: new Date().toISOString(),
        verified: false,
        impact: achievementData.impact || 'medium'
      };
      
      this.experienceData.achievements.push(newAchievement);
      await this.saveAchievements();
      
      // Notify subscribers
      this.notifySubscribers('achievement_added', newAchievement);
      
      return {
        success: true,
        data: newAchievement
      };
      
    } catch (error) {
      console.error('❌ Error adding achievement:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Add reference
  async addReference(userId, referenceData) {
    try {
      console.log('👥 Adding reference for:', userId);
      
      const newReference = {
        id: `ref_${Date.now()}`,
        ...referenceData,
        userId,
        dateAdded: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        status: 'pending',
        verified: false
      };
      
      this.experienceData.references.push(newReference);
      await this.saveReferences();
      
      // Notify subscribers
      this.notifySubscribers('reference_added', newReference);
      
      return {
        success: true,
        data: newReference
      };
      
    } catch (error) {
      console.error('❌ Error adding reference:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Calculate career progression
  calculateCareerProgression() {
    const experiences = (this.experienceData.workExperience || [])
      .slice() // avoid mutating original
      .sort((a, b) => new Date(a.startDate || 0) - new Date(b.startDate || 0));
    
    if (experiences.length === 0) {
      return {
        totalExperience: 0,
        careerLevel: 'Entry Level',
        progression: [],
        averageTenure: 0,
        skillsGrowth: []
      };
    }
    
    // Calculate total experience
    let totalMonths = 0;
    const progression = [];
    const allSkills = new Set();
    
    experiences.forEach((exp, index) => {
      const startDate = new Date(exp.startDate);
      const endDate = exp.endDate ? new Date(exp.endDate) : new Date();
      const months = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24 * 30));
      
      totalMonths += months;
      
      progression.push({
        position: exp.position,
        company: exp.company,
        duration: months,
        level: this.determineJobLevel(exp.position),
        skills: exp.skills || []
      });
      
      // Track skills growth
      if (exp.skills) {
        exp.skills.forEach(skill => allSkills.add(skill));
      }
    });
    
    const totalYears = Math.round(totalMonths / 12 * 10) / 10;
    const averageTenure = Math.round(totalMonths / experiences.length);
    
    return {
      totalExperience: totalYears,
      careerLevel: this.determineCareerLevel(totalYears, experiences),
      progression,
      averageTenure,
      skillsGrowth: Array.from(allSkills),
      companiesWorked: experiences.length,
      currentPosition: experiences[experiences.length - 1]?.position || 'Not specified'
    };
  }

  // Determine job level from position title
  determineJobLevel(position) {
    const title = position.toLowerCase();
    
    if (title.includes('senior') || title.includes('lead') || title.includes('principal')) {
      return 'Senior';
    } else if (title.includes('manager') || title.includes('director') || title.includes('head')) {
      return 'Management';
    } else if (title.includes('junior') || title.includes('intern') || title.includes('trainee')) {
      return 'Junior';
    } else if (title.includes('chief') || title.includes('vp') || title.includes('vice president')) {
      return 'Executive';
    } else {
      return 'Mid-level';
    }
  }

  // Determine career level
  determineCareerLevel(totalYears, experiences) {
    const hasManagementExp = experiences.some(exp => 
      this.determineJobLevel(exp.position) === 'Management' || 
      this.determineJobLevel(exp.position) === 'Executive'
    );
    
    if (totalYears >= 10 || hasManagementExp) {
      return 'Senior Level';
    } else if (totalYears >= 5) {
      return 'Mid Level';
    } else if (totalYears >= 2) {
      return 'Intermediate';
    } else {
      return 'Entry Level';
    }
  }

  // Get experience statistics
  getExperienceStatistics() {
    const careerProgression = this.calculateCareerProgression();
    
    const we = this.experienceData.workExperience || [];
    const pr = this.experienceData.projects || [];
    const ac = this.experienceData.achievements || [];
    const re = this.experienceData.references || [];
    return {
      totalWorkExperience: we.length,
      totalProjects: pr.length,
      totalAchievements: ac.length,
      totalReferences: re.length,
      verifiedReferences: re.filter(ref => ref && ref.verified).length,
      ...careerProgression,
      industryExperience: this.calculateIndustryExperience(),
      skillsFromExperience: this.extractSkillsFromExperience()
    };
  }

  // Calculate industry experience
  calculateIndustryExperience() {
    const industryMap = {};
    
    (this.experienceData.workExperience || []).forEach(exp => {
      if (exp && exp.industry) {
        if (!industryMap[exp.industry]) {
          industryMap[exp.industry] = 0;
        }
        
        const startDate = new Date(exp.startDate);
        const endDate = exp.endDate ? new Date(exp.endDate) : new Date();
        const months = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24 * 30));
        
        industryMap[exp.industry] += months;
      }
    });
    
    // Convert to years and sort
    const industries = Object.entries(industryMap)
      .map(([industry, months]) => ({
        industry,
        years: Math.round(months / 12 * 10) / 10
      }))
      .sort((a, b) => b.years - a.years);
    
    return industries;
  }

  // Extract skills from experience
  extractSkillsFromExperience() {
    const skillsMap = {};
    
    (this.experienceData.workExperience || []).forEach(exp => {
      if (exp && exp.skills) {
        exp.skills.forEach(skill => {
          if (!skillsMap[skill]) {
            skillsMap[skill] = {
              skill,
              experienceCount: 0,
              companies: []
            };
          }
          skillsMap[skill].experienceCount++;
          if (!skillsMap[skill].companies.includes(exp.company)) {
            skillsMap[skill].companies.push(exp.company);
          }
        });
      }
    });
    
    return Object.values(skillsMap)
      .sort((a, b) => b.experienceCount - a.experienceCount);
  }

  // Search experience records
  searchExperience(query) {
    const lowercaseQuery = query.toLowerCase();
    
    return {
      workExperience: (this.experienceData.workExperience || []).filter(exp =>
        exp && (
          (exp.position || '').toLowerCase().includes(lowercaseQuery) ||
          (exp.company || '').toLowerCase().includes(lowercaseQuery) ||
          (exp.description || '').toLowerCase().includes(lowercaseQuery)
        )
      ),
      projects: (this.experienceData.projects || []).filter(project =>
        project && (
          (project.title || '').toLowerCase().includes(lowercaseQuery) ||
          (project.description || '').toLowerCase().includes(lowercaseQuery) ||
          (project.technologies || []).some(tech => (tech || '').toLowerCase().includes(lowercaseQuery))
        )
      )
    };
  }

  // Get companies
  getCompanies() {
    return this.experienceData.companies;
  }

  // Search companies
  searchCompanies(query) {
    const lowercaseQuery = query.toLowerCase();
    return (this.experienceData.companies || []).filter(company =>
      company && (
        (company.name || '').toLowerCase().includes(lowercaseQuery) ||
        (company.industry || '').toLowerCase().includes(lowercaseQuery)
      )
    );
  }

  // Validate experience data
  validateExperienceData(experienceData) {
    const errors = {};
    
    if (!experienceData.position || experienceData.position.trim() === '') {
      errors.position = 'Position is required';
    }
    
    if (!experienceData.company || experienceData.company.trim() === '') {
      errors.company = 'Company is required';
    }
    
    if (!experienceData.startDate) {
      errors.startDate = 'Start date is required';
    }
    
    if (experienceData.endDate && new Date(experienceData.startDate) >= new Date(experienceData.endDate)) {
      errors.endDate = 'End date must be after start date';
    }
    
    if (experienceData.currentJob && experienceData.endDate) {
      errors.endDate = 'End date should not be set for current job';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Validate project data
  validateProjectData(projectData) {
    const errors = {};
    
    if (!projectData.title || projectData.title.trim() === '') {
      errors.title = 'Project title is required';
    }
    
    if (!projectData.description || projectData.description.trim() === '') {
      errors.description = 'Project description is required';
    }
    
    if (!projectData.startDate) {
      errors.startDate = 'Start date is required';
    }
    
    if (projectData.endDate && new Date(projectData.startDate) >= new Date(projectData.endDate)) {
      errors.endDate = 'End date must be after start date';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Subscribe to experience events
  subscribe(event, callback) {
    if (!this.subscribers.has(event)) {
      this.subscribers.set(event, []);
    }
    this.subscribers.get(event).push(callback);
  }

  // Unsubscribe from experience events
  unsubscribe(event, callback) {
    if (this.subscribers.has(event)) {
      const callbacks = this.subscribers.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  // Notify subscribers
  notifySubscribers(event, data) {
    if (this.subscribers.has(event)) {
      this.subscribers.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in subscriber callback for ${event}:`, error);
        }
      });
    }
  }

  // Save methods
  async saveWorkExperience() {
    try {
      await AsyncStorage.setItem('work_experience', JSON.stringify(this.experienceData.workExperience));
    } catch (error) {
      console.error('❌ Error saving work experience:', error);
    }
  }

  async saveProjects() {
    try {
      await AsyncStorage.setItem('user_projects', JSON.stringify(this.experienceData.projects));
    } catch (error) {
      console.error('❌ Error saving projects:', error);
    }
  }

  async saveAchievements() {
    try {
      await AsyncStorage.setItem('work_achievements', JSON.stringify(this.experienceData.achievements));
    } catch (error) {
      console.error('❌ Error saving achievements:', error);
    }
  }

  async saveReferences() {
    try {
      await AsyncStorage.setItem('user_references', JSON.stringify(this.experienceData.references));
    } catch (error) {
      console.error('❌ Error saving references:', error);
    }
  }

  // Cleanup
  cleanup() {
    this.subscribers.clear();
    this.offlineQueue = [];
    this.isInitialized = false;
  }
}

export default ExperienceManagementService;

